/**
 * PAT Evaluator - Auto Pass/Fail Engine
 * IET Code of Practice for In-Service Inspection and Testing of Electrical Equipment
 *
 * Appliance classification thresholds (Task 1.4.3):
 *  - Class I:   Earth continuity <= 0.1 Ω + lead resistance R, Insulation >= 1.0 MΩ
 *  - Class II:  Insulation >= 2.0 MΩ, no earth test required
 *  - Class III: Insulation >= 2.0 MΩ (no protective conductor / SELV)
 */

export const PAT_THRESHOLDS = {
  CLASS_I: {
    earthContinuityMax: 0.1,   // Ω above lead resistance
    insulationMin: 1.0,        // MΩ
    touchLeakageMax: 0.75      // mA (upper bound on leakage current)
  },
  CLASS_II: {
    insulationMin: 2.0,        // MΩ
    touchLeakageMax: 0.25      // mA
  },
  CLASS_III: {
    insulationMin: 2.0,        // MΩ
    touchLeakageMax: 0.25      // mA
  }
};

/** Standard lead resistance allowance (Ω) used when none recorded */
const DEFAULT_LEAD_RESISTANCE = 0.5;

const parseNum = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : n;
};

/**
 * Evaluate a single appliance against class-specific pass/fail thresholds.
 * @param {Object} appliance - Appliance record with measured values
 * @returns {Object} { status, failures: Array<string> }
 */
export const evaluateAppliance = (appliance) => {
  const cls = (appliance.applianceClass || '').toLowerCase();
  const failures = [];

  // Visual inspection gate - always required
  if (appliance.visualInspection === 'Fail') {
    failures.push('Visual inspection failed');
  }

  if (cls === 'class i') {
    // Earth continuity: measured must be <= lead resistance + 0.1 Ω
    const ec = parseNum(appliance.earthContinuity);
    const lead = parseNum(appliance.leadResistance) ?? DEFAULT_LEAD_RESISTANCE;
    if (ec !== null && ec > lead + PAT_THRESHOLDS.CLASS_I.earthContinuityMax) {
      failures.push(
        `Earth continuity (${ec} Ω) exceeds ${lead} + ${PAT_THRESHOLDS.CLASS_I.earthContinuityMax} Ω limit`
      );
    }
  } else if (cls === 'class ii' || cls === 'class iii') {
    // Touch/leakage for Class II/III must remain low
    const touch = parseNum(appliance.touchLeakage);
    if (touch !== null && touch > PAT_THRESHOLDS.CLASS_II.touchLeakageMax) {
      failures.push(`Leakage current (${touch} mA) exceeds limit`);
    }
  }

  // Insulation resistance applies to all classes
  const ir = parseNum(appliance.insulationResistance);
  const irMin = cls === 'class i' ? PAT_THRESHOLDS.CLASS_I.insulationMin : PAT_THRESHOLDS.CLASS_II.insulationMin;
  if (ir !== null && ir < irMin) {
    failures.push(`Insulation resistance (${ir} MΩ) below ${irMin} MΩ minimum`);
  }

  // Respect an explicit overall result override
  let status = failures.length === 0 ? 'PASS' : 'FAIL';
  if (appliance.testResult === 'NOT TESTED') {
    status = 'NOT TESTED';
  }

  return { status, failures };
};

/**
 * Evaluate a whole register.
 * @param {Array} appliances - List of appliance records
 * @returns {Object} { results, summary, passCount, failCount, notTestedCount }
 */
export const evaluateRegister = (appliances = []) => {
  const results = appliances.map(appliance => ({
    ...appliance,
    evaluation: evaluateAppliance(appliance)
  }));

  const passCount = results.filter(r => r.evaluation.status === 'PASS').length;
  const failCount = results.filter(r => r.evaluation.status === 'FAIL').length;
  const notTestedCount = results.filter(r => r.evaluation.status === 'NOT TESTED').length;

  return {
    results,
    summary: { passCount, failCount, notTestedCount, totalCount: results.length },
    registerPasses: failCount === 0 && notTestedCount === 0
  };
};

export default evaluateAppliance;